import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {EditUserDTO} from '../model/editUserDTO';
import {RegistrationDTO} from '../model/registrationDTO';
import {UserDTO} from '../model/userDTO';
import {ActivationDTO} from '../model/activationDTO';
import {HashDTO} from '../model/hashDTO';
import {EmailDTO} from '../model/emailDTO';
import {ChangePasswordDTO} from '../model/changePasswordDTO';
import {AddSecondUserDTO} from '../model/addSecondUserDTO';
import {IsUsernameAvailableResponse} from '../model/isUsernameAvailableResponse';
import {ShouldActivateResponse} from '../model/shouldActivateResponse';
import {PasswordDTO} from '../model/passwordDTO';
import {ConfirmationCodeDTO} from '../model/confirmationCodeDTO';
import {LoginResponseDTO} from '../model/loginResponseDTO';
import {map} from 'rxjs/operators';
import {EditSecondUserDTO} from '../model/editSecondUserDTO';

@Injectable()
export class UserService {

  private readonly secure = environment.baseUrl + environment.apiPrefix + 'users';
  private readonly unsecure = environment.baseUrl + environment.apiPrefix + 'users/unsecure';

  constructor(private http: HttpClient) {
  }


  /*
  * Secure
  * */

  getAll() {
    return this.http.get<Array<UserDTO>>(`${this.secure}`);
  }

  delete(id) {
    return this.http.delete<void>(`${this.secure}/${id}`);
  }

  update(id, editUserDTO: EditUserDTO) {
    return this.http.put<void>(`${this.secure}/${id}`, editUserDTO);
  }

  getCurrentUser() {
    return this.http.get<UserDTO>(`${this.secure}/user`);
  }

  checkPassword(dto: PasswordDTO) {
    return this.http.post<void>(`${this.secure}/check-password`, dto);
  }

  checkEmailAdress(dto: EmailDTO) {
    return this.http.post<void>(`${this.secure}/check-email`, dto);
  }

  checkEmailChangeConfirmationCode(dto: ConfirmationCodeDTO) {
    return this.http.post<void>(`${this.secure}/check-email-confirmationCode`, dto);
  }

  resendEmailConfirmationMail() {
    return this.http.get(`${this.secure}/resend-email-confirmation-mail`);
  }

  getSecondUser(clientId) {
    return this.http.get<UserDTO>(`${this.secure}/client/${clientId}/second-user`);
  }

  addSecondUser(clientId, dto: AddSecondUserDTO) {
    return this.http.post<void>(`${this.secure}/client/${clientId}/second-user`, dto);
  }

  removeSecondUser(clientId: number) {
    return this.http.delete<void>(`${this.secure}/client/${clientId}/second-user`);
  }

  editSecondUser(clientId: number, dto: EditSecondUserDTO) {
    return this.http.put<void>(`${this.secure}/client/${clientId}/second-user`, dto);
  }

  requestDataDeletion(userId: number) {
    return this.http.delete<void>(`${this.secure}/${userId}/request`);
  }

  undoRequestDataDeletion(userId: number) {
    return this.http.get<void>(`${this.secure}/${userId}/request/undo`);
  }

  /*
  * Unsecure
  * */

  register(user: RegistrationDTO) {
    return this.http.post<number>(`${this.unsecure}/register`, user);
  }

  activateUser(activation: ActivationDTO) {
    return this.http.post<HashDTO>(`${this.unsecure}/activate`, activation);
  }

  recover(request: EmailDTO) {
    return this.http.post<void>(`${this.unsecure}/request-password`, request);
  }

  checkLink(key) {
    return this.http.post<void>(`${this.unsecure}/recovery?key=${key}`, {key: key});
  }

  changePassword(request: ChangePasswordDTO) {
    return this.http.post<void>(`${this.unsecure}/change-password?key=${request.activationKey}`, request);
  }

  sendActivationMail(username) {
    return this.http.post<void>(`${this.unsecure}/send-activate-mail`, username);
  }

  sendActivationMailById(userId) {
    return this.http.get<void>(`${this.unsecure}/send-activate-mail-by-id?id=${userId}`);
  }

  isUsernameAvailable(username) {
    return this.http.post<IsUsernameAvailableResponse>(`${this.unsecure}/username`, {username: username});
  }

  shouldActivate(email: any) {
    return this.http.post<ShouldActivateResponse>(`${this.unsecure}/username/should-activate`, {username: email});
  }

  sendWelcomeEmail(userId, hash: HashDTO) {
    return this.http.post<void>(`${this.unsecure}/send-welcome-email/${userId}`, hash);
  }

  login(username: string, password: string) {
    return this.http.post<LoginResponseDTO>(`${this.unsecure}/login`, {username: username, password: password})
      .pipe(map((res) => {
        const token = res && res.token;
        if (token) {
          const username1 = res.username;
          sessionStorage.setItem('currentUser', JSON.stringify({
            username1,
            roles: res.role,
            token: res.token,
          }));
        }
      }));
  }

  /*
  * util
  * */
  isLoggedIn(): boolean {
    return this.getToken() !== '';
  }

  getToken(): string {
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
    const token = currentUser && currentUser.token;
    return token ? token : '';
  }

  logout(): void {
    sessionStorage.removeItem('currentUser');
  }

  isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  isUser(): boolean {
    return this.hasRole('USER') || this.hasRole('ADMIN');
  }

  isClient(): boolean {
    return this.hasRole('CLIENT');
  }

  isSecondUser() {
    return this.hasRole('SECOND_USER');
  }

  hasRole(role: string): boolean {
    return sessionStorage.currentUser ?
      JSON.parse(sessionStorage.currentUser)?.roles === role :
      false;
  }
}
